import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CreatedByCellComponent, UserAvatarModule } from '@shared/ui';

export type CaseState = 'pending' | 'read' | 'inProgress' | 'analyzed';

export interface CaseUserData {
  id: string;
  username: string;
  avatarUrl?: string;
}

export interface CaseData {
  id: string;
  name: string;
  state?: CaseState;
  createdAt: Date;
  createdBy?: CaseUserData;
  analysing?: CaseUserData[];
  analyzed?: CaseUserData[];
}

export enum CaseColumn {
  STATE = 'state',
  NAME = 'name',
  CREATED = 'created',
  ANALYSES = 'analyses',
  MIGRATE = 'migrate',
  COPY = 'copy',
  REMOVE = 'remove',
}

export type SortableCaseColumn = 'name' | 'created';
export type CaseSortDirection = 'asc' | 'desc';

export interface CaseSortChange {
  field: SortableCaseColumn;
  direction: CaseSortDirection;
}

@Component({
  selector: 'ts-case-list',
  templateUrl: './case-list.component.html',
  styleUrls: ['./case-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    UserAvatarModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    CreatedByCellComponent,
  ],
})
export class CaseListComponent implements OnChanges {
  @Input() items: CaseData[] = [];
  @Input() loading = true;
  @Input() noCases = false;
  @Input() canMigrate = false;
  @Input() canCopy = false;
  @Input() canRemove = false;
  // A plain technician may only migrate/remove their own cases.
  @Input() ownCasesOnly = false;
  @Input() currentUserId?: string;
  // Analysis-state columns (state + analyses) are hidden for technician roles.
  @Input() showAnalysisState = true;
  @Input() columns: CaseColumn[] = [
    CaseColumn.STATE,
    CaseColumn.NAME,
    CaseColumn.CREATED,
    CaseColumn.ANALYSES,
    CaseColumn.MIGRATE,
    CaseColumn.COPY,
    CaseColumn.REMOVE,
  ];

  @Input() activeSort: CaseSortChange | null = null;
  @Input() nameSortDisabled = false;

  @Output() selectItem = new EventEmitter<CaseData>();
  @Output() migrateItem = new EventEmitter<CaseData>();
  @Output() copyItem = new EventEmitter<CaseData>();
  @Output() removeItem = new EventEmitter<CaseData>();
  @Output() sortChange = new EventEmitter<CaseSortChange>();

  showColumnFilter = false;
  private hiddenColumns = new Set<CaseColumn>();

  // Store the container height when loading starts to prevent layout jumps
  containerHeight: number | null = null;

  @ViewChild('filterWrapper', { read: ElementRef }) filterWrapper?: ElementRef;

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showColumnFilter) {
      return;
    }

    const target = event.target as HTMLElement;
    const clickedInsideWrapper = this.filterWrapper?.nativeElement.contains(target);

    if (!clickedInsideWrapper) {
      this.showColumnFilter = false;
    }
  }

  toggleFilterDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showColumnFilter = !this.showColumnFilter;
  }

  get visibleColumns(): string[] {
    // Filter out hidden columns and action columns if not editable
    let visible = this.columns.filter((col) => !this.hiddenColumns.has(col));

    // Drop each action column the user is not allowed to perform, and the
    // analysts column when the role should not see analysis info. The state
    // column stays (it's useful to know which cases have been accessed).
    visible = visible.filter((col) => {
      if (col === CaseColumn.MIGRATE) return this.canMigrate;
      if (col === CaseColumn.COPY) return this.canCopy;
      if (col === CaseColumn.REMOVE) return this.canRemove;
      if (this.showAnalysisState === false && col === CaseColumn.ANALYSES) return false;
      return true;
    });

    // Always add columnFilter at the end
    return [...visible.map((col) => col.valueOf()), 'columnFilter'];
  }

  // A plain technician only sees migrate/remove on cases they created.
  canActOnCase(item: CaseData): boolean {
    return !this.ownCasesOnly || item.createdBy?.id === this.currentUserId;
  }

  get dataColumns(): CaseColumn[] {
    // Return only DATA columns from INPUT columns (filter out action columns).
    return this.columns.filter(
      (col) =>
        col !== CaseColumn.MIGRATE &&
        col !== CaseColumn.COPY &&
        col !== CaseColumn.REMOVE &&
        !(this.showAnalysisState === false && col === CaseColumn.ANALYSES)
    );
  }

  toggleColumn(column: CaseColumn): void {
    if (this.hiddenColumns.has(column)) {
      this.hiddenColumns.delete(column);
    } else {
      this.hiddenColumns.add(column);
    }
    // Trigger change detection
    this.hiddenColumns = new Set(this.hiddenColumns);
  }

  isColumnVisible(column: CaseColumn): boolean {
    return !this.hiddenColumns.has(column);
  }

  trackByCaseId(_index: number, row: CaseData): string {
    return row.id;
  }

  isSortable(field: SortableCaseColumn): boolean {
    return field === 'name' ? !this.nameSortDisabled : true;
  }

  arrowFor(field: SortableCaseColumn): CaseSortDirection | null {
    return this.activeSort?.field === field ? this.activeSort.direction : null;
  }

  onHeaderClick(field: SortableCaseColumn): void {
    if (!this.isSortable(field)) return;
    const isActiveAsc = this.activeSort?.field === field && this.activeSort.direction === 'asc';
    this.sortChange.emit({ field, direction: isActiveAsc ? 'desc' : 'asc' });
  }
}
